import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Plus, Lock } from "lucide-react";
import { toast } from "sonner";
import PageContainer from "@/components/PageContainer";
import BrandHeader from "@/components/BrandHeader";
import SoftButton from "@/components/SoftButton";
import MosaicCard from "@/components/MosaicCard";
import MosaicComposer from "@/components/MosaicComposer";
import { listMosaic, addMosaic, deleteMosaic } from "@/lib/mosaicVault";
import { MOSAIC_TYPES, MOSAIC_TYPE_ORDER } from "@/lib/mosaicData";
import { MOSAIC } from "@/constants/testIds";

const FILTERS = [{ id: "all", label: "All" }].concat(
  MOSAIC_TYPE_ORDER.map((id) => ({ id, label: MOSAIC_TYPES[id].plural }))
);

export default function Mosaic() {
  const [items, setItems] = useState([]);
  const [composing, setComposing] = useState(false);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    let cancelled = false;
    listMosaic().then((next) => {
      if (!cancelled) setItems(next);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    if (filter === "all") return items;
    return items.filter((i) => i.type === filter);
  }, [items, filter]);

  const handleSave = async (payload) => {
    try {
      const entry = await addMosaic(payload);
      setItems((it) => [entry, ...it]);
      setComposing(false);
      toast.success("Kept.", {
        description: "Another piece worth keeping.",
      });
    } catch (_e) {
      toast("Couldn't keep that just now.", {
        description: "Your reflections belong to you — try again in a moment.",
      });
    }
  };

  const handleDelete = async (id) => {
    await deleteMosaic(id);
    setItems((it) => it.filter((i) => i.id !== id));
    toast("Removed from your mosaic.");
  };

  return (
    <PageContainer testid={MOSAIC.root} className="pt-7">
      <BrandHeader compact />
      <section className="relative mb-6">
        <div aria-hidden className="absolute right-1 top-0 w-28 h-28">
          <span className="absolute right-0 top-0 w-20 h-20 rounded-3xl rotate-6" style={{ background: "rgba(247,228,184,0.72)" }} />
          <span className="absolute right-8 top-5 w-20 h-20 rounded-3xl -rotate-6" style={{ background: "rgba(222,189,208,0.72)" }} />
        </div>
        <h1 className="relative text-[47px] leading-none tracking-[-0.035em]" style={{ color: "var(--un-plum-deep)", fontFamily: "Lora, serif" }}>
          Mosaic
        </h1>
        <p className="relative mt-2 italic text-[31px] leading-none" style={{ color: "var(--un-burgundy)", fontFamily: "Cormorant Garamond, serif" }}>
          Your soul, in pieces worth keeping.
        </p>
        <p className="relative mt-5 max-w-[370px] text-[15px] leading-relaxed" style={{ color: "var(--un-text-2)" }}>
          Build a quiet picture of what matters to you — words, songs, films, books, places, memories, people and moments that help you remember who you are.
        </p>
      </section>

      {/* Privacy strip */}
      <div
        className="rounded-2xl p-3 mb-5 flex items-center gap-2.5"
        style={{ background: "rgba(74,32,64,0.05)" }}
      >
        <Lock size={14} style={{ color: "var(--un-plum)" }} />
        <p
          className="text-[12px] leading-snug"
          style={{ color: "var(--un-text-2)" }}
        >
          Your Soul Mosaic lives <strong>only on this device</strong>, kept encrypted at rest. Sharing is never automatic; later, you can choose individual pieces to carry, export, or share.
        </p>
      </div>

      {/* Add */}
      {!composing && (
        <SoftButton
          data-testid={MOSAIC.addBtn}
          variant="primary"
          size="lg"
          iconLeft={<Plus size={18} />}
          onClick={() => setComposing(true)}
          className="w-full mb-5"
        >
          Add to my Mosaic
        </SoftButton>
      )}

      <AnimatePresence>
        {composing && (
          <MosaicComposer
            onSave={handleSave}
            onCancel={() => setComposing(false)}
          />
        )}
      </AnimatePresence>

      {/* Filter chips */}
      {items.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-2 mb-5 -mx-1 px-1">
          {FILTERS.map((f) => {
            const active = filter === f.id;
            return (
              <button
                key={f.id}
                data-testid={`${MOSAIC.filterPrefix}${f.id}`}
                onClick={() => setFilter(f.id)}
                className="shrink-0 px-3.5 py-1.5 rounded-full text-xs font-medium un-press transition-all"
                style={{
                  background: active ? "var(--un-plum)" : "rgba(74,32,64,0.05)",
                  color: active ? "#FDFBF7" : "var(--un-text-2)",
                  fontFamily: "Manrope, sans-serif",
                }}
              >
                {f.label}
              </button>
            );
          })}
        </div>
      )}

      {/* Empty state or stack */}
      {filtered.length === 0 ? (
        <EmptyMosaic
          subtle={items.length > 0}
          onAdd={() => setComposing(true)}
        />
      ) : (
        <div className="flex flex-col gap-7 pb-10 pt-2">
          {filtered.map((item, idx) => (
            <MosaicCard
              key={item.id}
              item={item}
              onDelete={handleDelete}
              index={idx}
            />
          ))}
        </div>
      )}
    </PageContainer>
  );
}

function EmptyMosaic({ onAdd, subtle }) {
  return (
    <motion.div
      data-testid={MOSAIC.emptyState}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="text-center pt-6 pb-12 px-2"
    >
      {/* Layered paper illustration */}
      <div className="relative h-32 mx-auto mb-6" style={{ width: "180px" }}>
        <div
          className="absolute left-2 top-2 right-6 bottom-3 rounded-xl"
          style={{
            background: "#E6D2BF",
            transform: "rotate(-5deg)",
            boxShadow: "0 6px 18px rgba(74,45,72,0.10)",
          }}
        />
        <div
          className="absolute left-6 top-3 right-2 bottom-2 rounded-xl"
          style={{
            background: "#F4E6C8",
            transform: "rotate(3deg)",
            boxShadow: "0 6px 18px rgba(74,45,72,0.10)",
          }}
        />
        <div
          className="absolute left-4 top-0 right-4 bottom-5 rounded-xl flex items-center justify-center"
          style={{
            background: "#E0C8D0",
            transform: "rotate(-1deg)",
            boxShadow: "0 8px 22px rgba(74,45,72,0.12)",
          }}
        >
          <span
            className="text-[28px] italic"
            style={{
              color: "var(--un-burgundy)",
              fontFamily: "Cormorant Garamond, serif",
            }}
          >
            …
          </span>
        </div>
      </div>
      <p
        className="text-[17px] font-medium mb-1.5"
        style={{
          color: "var(--un-text-1)",
          fontFamily: "Manrope, sans-serif",
        }}
      >
        {subtle ? "Nothing here yet" : "Your Mosaic is waiting"}
      </p>
      <p
        className="italic text-[17px] leading-relaxed max-w-[300px] mx-auto mb-5"
        style={{
          color: "var(--un-text-3)",
          fontFamily: "Cormorant Garamond, serif",
        }}
      >
        Keep one song, one place, one line, one memory, one film that changed something in you. Over time, the pieces can help you see what brings you back to yourself.
      </p>
      {!subtle && (
        <SoftButton variant="secondary" size="sm" onClick={onAdd}>
          Begin your mosaic
        </SoftButton>
      )}
    </motion.div>
  );
}
